import React, { useState, useEffect, useRef } from 'react';

// Custom hook to compare previous values
function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export function usePersistedState<T>(key: string, initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  // Initialize state function to avoid reading localStorage on every render
  const [state, setState] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (item === null || item === 'undefined') {
        return initialValue;
      }
      
      const parsed = JSON.parse(item);
      
      // Safety Check: If initialValue is an array, ensure parsed is also an array
      if (Array.isArray(initialValue)) {
        if (!Array.isArray(parsed)) {
            console.warn(`Data mismatch for key "${key}". Expected array, got ${typeof parsed}. Resetting to default.`);
            return initialValue;
        }
        // Filter out nulls/undefineds from the loaded array
        return parsed.filter((i: any) => i !== null && i !== undefined) as T;
      }

      return parsed;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Ref to track if the update came from cross-tab sync to avoid writing it back immediately
  const isSyncUpdate = useRef(false);

  // Effect to write to localStorage when state changes (only if it wasn't a sync update)
  useEffect(() => {
    if (isSyncUpdate.current) {
      isSyncUpdate.current = false;
      return;
    }

    try {
      if (state === undefined) {
        window.localStorage.removeItem(key);
      } else {
        const valueToStore = JSON.stringify(state);
        const currentStored = window.localStorage.getItem(key);
        // Only write if different to reduce overhead
        if (valueToStore !== currentStored) {
             window.localStorage.setItem(key, valueToStore);
        }
      }
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, state]);

  // Effect to listen for changes in other tabs/windows AND poll for changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        try {
          const newValue = JSON.parse(e.newValue);
          isSyncUpdate.current = true;
          setState(newValue);
        } catch (error) {
          console.error("Error syncing state across tabs:", error);
        }
      }
    };

    // Polling function to handle cases where storage event might be missed or delayed
    const pollLocalStorage = () => {
        try {
            const storedValue = window.localStorage.getItem(key);
            if (storedValue) {
                // This is a simplified check. In a real app with huge state, this might be heavy, 
                // but for this scale it ensures reliability.
                // We rely on the interval effect below for the actual update logic.
            }
        } catch(e) { }
    };

    // Force re-sync on window focus to ensure data is fresh if the user comes back to the tab
    const onFocus = () => {
        try {
            const stored = window.localStorage.getItem(key);
            if(stored) {
                const parsed = JSON.parse(stored);
                setState(prev => {
                    if(JSON.stringify(prev) !== stored) {
                        isSyncUpdate.current = true;
                        return parsed;
                    }
                    return prev;
                });
            }
        } catch(e) {}
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', onFocus);
    
    // Fallback Polling every 1 second
    const interval = setInterval(() => {
        const stored = window.localStorage.getItem(key);
        if(stored) {
            setState(prev => {
                if(JSON.stringify(prev) !== stored) {
                    isSyncUpdate.current = true;
                    return JSON.parse(stored);
                }
                return prev;
            });
        }
    }, 1000);

    return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('focus', onFocus);
        clearInterval(interval);
    };
  }, [key]);

  return [state, setState];
}